import React, { useEffect, useState } from "react";
import { PortalHeader, PortalBody } from "@/components/PortalUI";
import api, { formatApiError } from "@/lib/api";
import { CheckCircle2, XCircle, Ban, ChevronRight, ShieldCheck } from "lucide-react";

const STATUS_COLORS = {
  submitted: "dt6-chip-primary",
  in_review: "dt6-chip-secondary",
  approved: "dt6-chip-success",
  declined: "dt6-chip-danger",
  revoked: "dt6-chip-danger",
};

export default function AdminAlignmentBoard() {
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [filter, setFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");

  const load = () => {
    const q = filter ? `?status=${filter}` : "";
    api.get(`/admin/alignment${q}`).then((r) => {
      setRows(r.data.applications || []);
      setCounts(r.data.counts || {});
    }).catch(() => {});
  };
  useEffect(load, [filter]);

  const decide = async (decision) => {
    setBusy(true); setErr(""); setMsg("");
    try {
      const { data } = await api.post(`/admin/alignment/${selected.id}/decide`, {
        decision, decision_note: note,
      });
      setMsg(`Application ${data.status}.`);
      setSelected(data.application);
      setNote("");
      load();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PortalHeader eyebrow="/ ADMIN · VETTING" title="Alignment applications" testId="alignment-board-header">
        <button className="dt6-btn text-xs" onClick={load} data-testid="alignment-refresh">Refresh</button>
      </PortalHeader>
      <PortalBody>
        <div className="flex gap-2 mb-4 flex-wrap">
          {["", "submitted", "in_review", "approved", "declined", "revoked"].map((s) => (
            <button
              key={s || "all"}
              className={`dt6-btn text-xs ${filter === s ? "dt6-btn-primary" : ""}`}
              onClick={() => setFilter(s)}
              data-testid={`alignment-filter-${s || "all"}`}
            >
              {(s || "all").toUpperCase()} · {counts[s] ?? (s ? 0 : Object.values(counts).reduce((a, b) => a + b, 0))}
            </button>
          ))}
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-2 dt6-card p-0 overflow-auto">
            <table className="dt6-table w-full" data-testid="alignment-list">
              <thead>
                <tr className="text-left">
                  <th>Org</th><th>Type</th><th>State</th><th>Submitted</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan="6" className="text-center py-8 text-[color:var(--dt6-muted)]">No applications yet.</td></tr>
                )}
                {rows.map((r) => (
                  <tr key={r.id} data-testid={`alignment-row-${r.id}`} className="cursor-pointer" onClick={() => { setSelected(r); setNote(""); setMsg(""); setErr(""); }}>
                    <td><div className="font-medium">{r.org_name}</div><div className="text-xs text-[color:var(--dt6-muted)]">{r.contact_email}</div></td>
                    <td className="font-mono text-xs uppercase">{r.org_type}</td>
                    <td className="font-mono text-xs">{r.states}</td>
                    <td className="font-mono text-xs">{(r.created_at || "").slice(0, 10)}</td>
                    <td><span className={`dt6-chip ${STATUS_COLORS[r.status] || ""}`}>{r.status}</span></td>
                    <td><ChevronRight size={14} /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <aside className="dt6-card p-6" data-testid="alignment-detail">
            {!selected ? (
              <p className="text-sm text-[color:var(--dt6-muted)]">Select an application to review.</p>
            ) : (
              <>
                <div className="dt6-chip dt6-chip-primary mb-3">/ APPLICATION</div>
                <h3 className="font-heading text-xl font-semibold" data-testid="al-detail-org">{selected.org_name}</h3>
                <div className="text-xs text-[color:var(--dt6-muted)] font-mono mb-4">{selected.contact_email}</div>

                <dl className="text-xs space-y-2 mb-4">
                  <div><dt className="font-mono uppercase text-[color:var(--dt6-muted)]">Principal</dt><dd>{selected.principal_name} · {selected.principal_role}</dd></div>
                  <div><dt className="font-mono uppercase text-[color:var(--dt6-muted)]">States</dt><dd>{selected.states}</dd></div>
                  <div><dt className="font-mono uppercase text-[color:var(--dt6-muted)]">Office / mission</dt><dd>{selected.office_or_mission}</dd></div>
                  <div><dt className="font-mono uppercase text-[color:var(--dt6-muted)]">Mission statement</dt><dd className="whitespace-pre-wrap">{selected.mission_statement}</dd></div>
                  <div><dt className="font-mono uppercase text-[color:var(--dt6-muted)]">Signature</dt><dd>{selected.standard_signature} · {(selected.signature_at || "").slice(0, 19).replace("T", " ")}</dd></div>
                  <div><dt className="font-mono uppercase text-[color:var(--dt6-muted)]">Tier interest</dt><dd>{selected.tier_interest || "—"}</dd></div>
                  <div><dt className="font-mono uppercase text-[color:var(--dt6-muted)]">Current status</dt><dd><span className={`dt6-chip ${STATUS_COLORS[selected.status] || ""}`}>{selected.status}</span></dd></div>
                  {selected.decision_note && (
                    <div><dt className="font-mono uppercase text-[color:var(--dt6-muted)]">Reviewer note</dt><dd>{selected.decision_note}</dd></div>
                  )}
                </dl>

                {selected.status === "submitted" || selected.status === "in_review" ? (
                  <>
                    <label className="dt6-label">Decision note (required for decisions — mission/conduct-based only)</label>
                    <textarea rows={3} className="dt6-textarea" value={note}
                              onChange={(e) => setNote(e.target.value)}
                              placeholder="Mission or conduct rationale only. Never protected characteristics."
                              data-testid="al-detail-note" />
                    <div className="flex flex-col gap-2 mt-3">
                      <button className="dt6-btn dt6-btn-primary" onClick={() => decide("approve")} disabled={busy} data-testid="al-detail-approve">
                        <CheckCircle2 size={14} /> Approve
                      </button>
                      <button className="dt6-btn" onClick={() => decide("decline")} disabled={busy} data-testid="al-detail-decline">
                        <XCircle size={14} /> Decline
                      </button>
                    </div>
                  </>
                ) : selected.status === "approved" ? (
                  <button className="dt6-btn" onClick={() => decide("revoke")} disabled={busy} data-testid="al-detail-revoke">
                    <Ban size={14} /> Revoke approval
                  </button>
                ) : (
                  <p className="text-xs text-[color:var(--dt6-muted)]">Decision is final.</p>
                )}

                {msg && <div className="text-xs text-[color:var(--dt6-success)] mt-3" data-testid="al-detail-msg">{msg}</div>}
                {err && <div className="text-xs text-[color:var(--dt6-danger)] mt-3" data-testid="al-detail-err">{err}</div>}
              </>
            )}
          </aside>
        </div>
      </PortalBody>
    </>
  );
}
